import { FC, useMemo, useRef, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

import { Cross, ZoomIn, ZoomOut } from '@assets/icons';
import { Modal } from '@components/index';
import {
  MAX_ZOOM_LEVEL,
  MIN_ZOOM_LEVEL,
  ZOOM_STEP,
} from '@pages/home-page/constants';

interface DocumentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  imageUrls: string[];
  externalAssetId?: string;
}

const DocumentPreviewModal: FC<DocumentPreviewModalProps> = ({
  isOpen,
  onClose,
  fileName,
  imageUrls,
  externalAssetId,
}) => {
  const [selectedPage, setSelectedPage] = useState(imageUrls[0]);
  const [zoomLevel, setZoomLevel] = useState(1);
  const parentRef = useRef<HTMLDivElement>(null);

  const selectedPageIndex = useMemo(() => {
    return imageUrls.indexOf(selectedPage) + 1;
  }, [selectedPage, imageUrls]);

  const imagePreviewVirtualizer = useVirtualizer({
    count: imageUrls.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 260,
    overscan: 5,
  });

  const handleZoomIn = (e: React.PointerEvent<Element>) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM_LEVEL));
  }; // Limit max zoom level

  const handleZoomOut = (e: React.PointerEvent<Element>) => {
    e.stopPropagation();
    setZoomLevel(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM_LEVEL));
  }; // Limit min zoom level

  return (
    <Modal isOpen={isOpen} onClose={onClose} showCloseButton={false}>
      <div className="flex flex-col w-[85vw] h-[90vh] pointer-events-auto overflow-auto">
        <div className="flex flex-row p-4 border-b justify-between items-center h-20">
          <div className="flex flex-col gap-1">
            <div className="flex justify-center align-center items-center gap-2 text-md font-semibold">
              <div className="break-all">{fileName}</div>
              {externalAssetId && (
                <div className="flex items-center text-sm text-blue-2 border-blue-3 bg-blue-4 rounded-xl p-2 mt-1 font-semibold w-fit max-w-[200px] overflow-hidden whitespace-nowrap">
                  {`TAB ID: ${externalAssetId}`}
                </div>
              )}
            </div>
            <div className="text-sm font-semibold">{`Page ${selectedPageIndex} of ${imageUrls.length}`}</div>
          </div>
          <div className="flex gap-4 items-center">
            <ZoomIn className="cursor-pointer" onPointerDown={handleZoomIn} />
            <ZoomOut className="cursor-pointer" onPointerDown={handleZoomOut} />
            <div className="border-r border-gray-6 h-7" />
            <Cross
              className="cursor-pointer text-gray-14 h-6 w-6"
              onPointerDown={(e: React.PointerEvent<Element>) => {
                e.stopPropagation();
                onClose();
              }}
            />
          </div>
        </div>
        <div className="flex flex-1">
          <div
            ref={parentRef}
            className="flex flex-col gap-2 border-r p-5 w-[18%] overflow-auto"
          >
            <div className="relative">
              {imagePreviewVirtualizer.getVirtualItems().map(virtualRow => {
                const imageUrl = imageUrls[virtualRow.index];
                const isSelected = imageUrl === selectedPage;
                const selectedFileClass = isSelected ? 'shadow-2xl' : '';
                const selectedPageNumberClass = isSelected ? 'bg-lighter' : '';

                return (
                  <div
                    key={virtualRow.index}
                    className="cursor-pointer absolute top-0 left-0 w-full"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    onMouseDown={() => {
                      setSelectedPage(imageUrl);
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt={`Page ${virtualRow.index + 1}`}
                      className={`w-full h-[220px] border rounded-md ${selectedFileClass}`}
                    />
                    <div
                      className={`absolute left-1/2 transform -translate-x-1/2 text-xs font-bold p-1 ${selectedPageNumberClass}`}
                    >
                      {virtualRow.index + 1}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-col w-full h-[calc(90vh-100px)] items-center overflow-auto my-2">
            {selectedPage && (
              <img
                src={selectedPage}
                alt="Selected document preview"
                className="w-[55%] h-[100%] border shadow-lg overflow-auto object-contain transition-transform duration-300 ease-in-out"
                style={{
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: `${zoomLevel < 1 ? 'center' : 'top'}`,
                }}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentPreviewModal;
